import { EmissionFactor, ReportingPeriod } from '@esg/esg-global-types';
import {
  MetricRecordDataLevel,
  MetricRecordReportingEntity,
  QualitativeMetricRecordInputData,
  QuantitativeMetricRecordInputData
} from '../services/metric_record.service';
import { getEmissionFactorForMetricRecordParameters } from '../../lib/app/emission_factor';
import { reportingPeriodFilterByGroup } from '../../lib/metric_capture/reporting_period';
import { MetricExtended } from '../../lib/metric_capture/metric';
import { isRegion } from '../../lib/metric_capture/region';
import { isSiteExtended } from '../../lib/metric_capture/site';
import type { SiteExtended } from '../../lib/metric_capture/site';
import {
  CalculatedMetricRecordClientData,
  CreateMetricRecordClientData
} from '@esg/syntiro-api-core';

/**
 * Map raw qualitative metric record data into client request data required by API.
 * @param {MetricExtended} metric Metric data of metric record.
 * @param {ReportingPeriodGroup} reporting_period_group Reporting period group of metric record.
 * @param {boolean} status Compliance status of qualitative metric record.
 * @param {string} value Value of qualitative metric record.
 * @returns {CreateMetricRecordClientData}
 */
export const mapQualitativeMetricRecordInputForRequest = (
  input_data: QualitativeMetricRecordInputData
): CreateMetricRecordClientData => {
  return {
    value: input_data.value,
    metric: input_data.metric,
    metric_group: { id: input_data.metric.metric_group.id },
    standard: { id: input_data.metric.standard.id },
    reporting_period_group: { id: input_data.reporting_period_group.id },
    reporting_period: null,
    external_company: null,
    country: null,
    region: null,
    division: null,
    subdivision: null,
    site: null,
    source: 'manual',
    status: input_data.status
  };
};

/**
 * Derives the country ID applicable to a metric record from its reporting entity and data level.
 * Used to select the correct country-specific emission factor during capture.
 * @param reportingEntity The entity the metric record is captured against.
 * @param dataLevel The organisational level at which the record is captured.
 */
export function getCountryIdForMetricRecord(
  reportingEntity: MetricRecordReportingEntity | null,
  dataLevel: MetricRecordDataLevel | null
): string | null {
  if (!reportingEntity) return null;
  if (isRegion(reportingEntity)) return reportingEntity.country?.id ?? null;
  if (dataLevel === 'national') return reportingEntity.id;
  if (isSiteExtended(reportingEntity)) return reportingEntity.region.country?.id ?? null;
  return null;
}

/**
 * Derives the country ID for a site from its resolved region.
 * Falls back to null for legacy sites that have no country on their region,
 * @param site The fully-resolved SiteExtended, or null/undefined if not available.
 */
export function getCountryIdForSite(site: SiteExtended | null | undefined): string | null {
  if (!site) return null;
  return site.region?.country?.id ?? null;
}

/**
 * Map raw quantitative metric record data into client request data required by API.
 * @param {MetricExtended} metric Metric data of metric record.
 * @param {MetricRecordDataLevel} data_level Data level of metric record.
 * @param {MetricRecordReportingEntity} reporting_entity Reporting entity of metric record.
 * @param {ReportingPeriod | null} reporting_period Reporting period of metric record, null if averaging over year.
 * @param {number} value Value of quantitative metric record.
 * @param {Array<EmissionFactor>} emission_factors Emission factors to search through for metric and reporting period matching.
 * @param {ExternalCompany | null} external_company External company of metric record.
 * @param {string | undefined} metric_record_id ID of metric record if request data relates to existing one in database.
 * @param {string | null | undefined} note Optional note for metric record.
 * @param {number | undefined} proportion Optional custom client allocation value of metric record.
 * @param {boolean | undefined} average_over_year Optionally specify if metric record data is being averaged over year (reporting period group).
 * @returns {CreateMetricRecordClientData}
 */
export const mapQuantitativeMetricRecordInputForRequest = (
  input_data: QuantitativeMetricRecordInputData
): CreateMetricRecordClientData => {
  const metricRecordCountryId = getCountryIdForMetricRecord(
    input_data.reporting_entity,
    input_data.data_level
  );

  // Get relevant emission factor, if exists, for primary metric and reporting period.
  const emission_factor: EmissionFactor | null = input_data.reporting_period
    ? getEmissionFactorForMetricRecordParameters(
        input_data.emission_factors,
        input_data.metric,
        input_data.reporting_period,
        metricRecordCountryId
      )
    : null;

  // Convert calculated metrics to calculated metric record request data.
  let calculated_metric_record_data: Array<CalculatedMetricRecordClientData> = [];
  if (input_data.calculated_metrics && input_data.calculated_metrics.length) {
    calculated_metric_record_data = input_data.calculated_metrics.map(
      (calculated_metric: MetricExtended) => {
        const calculated_metric_emission_factor: EmissionFactor | null = input_data.reporting_period
          ? getEmissionFactorForMetricRecordParameters(
              input_data.emission_factors,
              calculated_metric,
              input_data.reporting_period,
              metricRecordCountryId
            )
          : null;
        return {
          calculated_metric: { id: calculated_metric.id },
          calculated_metric_group: { id: calculated_metric.metric_group.id },
          emission_factor: calculated_metric_emission_factor
            ? { id: calculated_metric_emission_factor.id }
            : undefined,
          scope: input_data.external_company
            ? { id: input_data.external_company.scope.id }
            : calculated_metric.scope
              ? { id: calculated_metric.scope.id }
              : null
        };
      }
    );
  }

  return {
    value: input_data.value.toString(),
    metric: { id: input_data.metric.id },
    metric_group: { id: input_data.metric.metric_group.id },
    standard: { id: input_data.metric.standard.id },
    reporting_period: { id: input_data.reporting_period ? input_data.reporting_period.id : '' },
    country: !input_data.reporting_entity
      ? null
      : 'country' in input_data.reporting_entity
        ? { id: input_data.reporting_entity.country.id }
        : input_data.data_level === 'national'
          ? { id: input_data.reporting_entity.id }
          : null,
    region: !input_data.reporting_entity
      ? null
      : 'region' in input_data.reporting_entity
        ? { id: input_data.reporting_entity.region.id }
        : input_data.data_level === 'regional'
          ? { id: input_data.reporting_entity.id }
          : null,
    division: !input_data.reporting_entity
      ? null
      : 'division' in input_data.reporting_entity
        ? { id: input_data.reporting_entity.division.id }
        : input_data.data_level === 'divisional'
          ? { id: input_data.reporting_entity.id }
          : null,
    subdivision: !input_data.reporting_entity
      ? null
      : 'subdivision' in input_data.reporting_entity
        ? { id: input_data.reporting_entity.subdivision.id }
        : input_data.data_level === 'subdivisional'
          ? { id: input_data.reporting_entity.id }
          : null,
    site: !input_data.reporting_entity
      ? null
      : input_data.data_level === 'site'
        ? { id: input_data.reporting_entity.id }
        : null,
    emission_factor: emission_factor ? { id: emission_factor.id } : undefined,
    external_company: input_data.external_company ? { id: input_data.external_company.id } : null,
    scope: input_data.external_company
      ? { id: input_data.external_company.scope.id }
      : input_data.metric.scope
        ? { id: input_data.metric.scope.id }
        : null,
    note: input_data.note ? input_data.note : '',
    proportion:
      input_data.proportion !== null && input_data.proportion !== undefined
        ? input_data.proportion / 100
        : 1,
    is_averaged: input_data.average_over_year ?? false,
    source: input_data.source,
    calculated_metric_records: calculated_metric_record_data
  };
};

/**
 * Map averaged metric record input data to client data required for bulk create API call for each reporting period in given reporting period group.
 * @param {QuantitativeMetricRecordInputData} input_data Base raw data to create list of quantitative metric records to create.
 * @param {string} reporting_period_group_id Reporting period group that metric records are being averaged across.
 * @param {Array<ReportingPeriods>} reporting_periods List of reporting periods to identify reporting period group children within.
 * @returns {Array<CreateMetricRecordClientData>}
 */
export const mapAveragedMetricRecordInputForRequest = (
  input_data: QuantitativeMetricRecordInputData,
  reporting_period_group_id: string,
  reporting_periods: Array<ReportingPeriod>
): Array<CreateMetricRecordClientData> => {
  const reporting_periods_for_group: Array<ReportingPeriod> = reportingPeriodFilterByGroup(
    reporting_period_group_id,
    reporting_periods
  );
  const averaged_value: number = input_data.value / reporting_periods_for_group.length;
  const averaged_metric_record_data: Array<CreateMetricRecordClientData> =
    reporting_periods_for_group.map((reporting_period: ReportingPeriod) => {
      return mapQuantitativeMetricRecordInputForRequest({
        ...input_data,
        reporting_period: reporting_period,
        value: averaged_value,
        average_over_year: true
      });
    });
  return averaged_metric_record_data;
};
